import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, input, Input, InputSignal, OnChanges } from '@angular/core';
import { API_TIMEZONE } from '@app/core/models/constants/date-time';
import { ScheduleHoursToDisplay } from '@app/core/models/interfaces/schedule-hours-to-display.interface';
import { CronService } from '@app/modules/schedule/services/cron.service';
import { ClassInput } from '@feedonomics/frontend-components';
import { faChevronDown, faChevronRight, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { Observable, Subscription } from 'rxjs';

interface HoursInterface {
    server: string;
    local: string;
}

@Component({
    selector: 'fdx-cron-viewer',
    templateUrl: './cron-viewer.component.html',
    styleUrl: './cron-viewer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CronViewerComponent implements OnChanges {
    @Input() classes: ClassInput;
    @Input() cron: string | null = null;
    @Input() cronTimezone: string | null = null;
    @Input() displaySettingsTimeZone: boolean = false;
    @Input() schedulePaused: boolean = false;
    @Input() condensed: boolean = false;
    scheduleToggle$: InputSignal<Observable<boolean>> = input<Observable<boolean>>();

    scheduleHoursToDisplay: ScheduleHoursToDisplay[] = [];

    hours: HoursInterface[] = [];
    protected hoursList: boolean = false;

    private scheduleToggleSubscription: Subscription;

    hoursListClosedIcon: IconDefinition = faChevronRight;
    hoursListOpenedIcon: IconDefinition = faChevronDown;

    constructor(
            private readonly cdr: ChangeDetectorRef,
            private readonly cronService: CronService
        ) {
            effect(() => {
                if (this.scheduleToggleSubscription) {
                    this.scheduleToggleSubscription.unsubscribe();
                }
                if (this.scheduleToggle$()) {
                    this.scheduleToggleSubscription = this.scheduleToggle$().subscribe((open) => {
                        this.hoursList = open;
                        this.cdr.markForCheck();
                    });
                }
            });
        }

    ngOnChanges(): void {
        this.hours = [];
        this.processCron();
    }

    private processCron(): void {

        if (this.cron === null) {
            return;
        }

        if (this.cronTimezone === null) {
            this.scheduleHoursToDisplay = this.cronService.getScheduleHoursToDisplay(this.cron, API_TIMEZONE);
        } else {
            this.scheduleHoursToDisplay = this.cronService.getScheduleHoursToDisplay(this.cron, this.cronTimezone);
        }

        for (const schedule of this.scheduleHoursToDisplay) {
            const hour: HoursInterface = {
                local: `${schedule.userTime.timeToDisplay} ${schedule.userTime.abbreviatedTimeZoneName}`,
                server: `${schedule.apiTime.timeToDisplay} ${schedule.apiTime.abbreviatedTimeZoneName}`
            };

            if (!this.schedulePaused) {
                if (schedule.userTime.dayBefore) {
                    hour.server += ' (next day)';
                }

                if (schedule.apiTime.dayBefore) {
                    hour.server += ' (prev day)';
                }
            }

            this.hours.push(hour);
        }
    }

    getScheduledDays(): string {
        if (!this.cron) {
            return '-';
        }

        const scheduledDaysFromCron = this.cronService.getScheduledDays(this.cron, this.cronTimezone);

        if (this.condensed) {
            return this.condenseScheduledDays(scheduledDaysFromCron);
        }

        return scheduledDaysFromCron;
    }

    private condenseScheduledDays(scheduledDays: string): string {

        const daysOfWeek = scheduledDays.split(', ');

        if (daysOfWeek.length === 0) {
            return '-';
        }

        if (daysOfWeek[0] === 'Every Day') {
            return 'Every day';
        }

        if (daysOfWeek.includes('Saturday') && daysOfWeek.includes('Sunday')) {
            return 'Weekends';
        }

        return daysOfWeek.map((day) => {
            switch (day) {
                case 'Sunday':
                    return 'Sun';
                case 'Monday':
                    return 'Mon';
                case 'Tuesday':
                    return 'Tues';
                case 'Wednesday':
                    return 'Wed';
                case 'Thursday':
                    return 'Thurs';
                case 'Friday':
                    return 'Fri';
                case 'Saturday':
                    return 'Sat';
                default:
                    // If it falls through, then it's Every Day at increment.
                    return day;
            }
        }).join(', ');
    }

    toggleHours(): void {
        this.hoursList = !this.hoursList;
    }
}
